import * as React from 'react'

export interface QueryResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  /** Re-run the fetcher. Safe to call from event handlers. */
  refetch: () => Promise<void>
  /** Patch local data without a round trip, for optimistic updates. */
  setData: React.Dispatch<React.SetStateAction<T | null>>
}

/**
 * Minimal async-data hook. Deliberately not a caching library: every page here
 * reads a handful of rows for one team, and a stale-cache bug in a portal that
 * shows metrics is worse than an extra query.
 *
 * `deps` behaves like a useEffect dependency list — include the team id so
 * switching teams refetches.
 */
export function useQuery<T>(fetcher: () => Promise<T>, deps: React.DependencyList): QueryResult<T> {
  const [data, setData] = React.useState<T | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  // Hold the latest fetcher in a ref so refetch() is stable even though
  // callers pass a fresh closure on every render.
  const fetcherRef = React.useRef(fetcher)
  fetcherRef.current = fetcher

  // Guards against a slow earlier request resolving after a newer one and
  // overwriting fresher data (e.g. rapid team switching).
  const generation = React.useRef(0)

  const run = React.useCallback(async (showSpinner: boolean) => {
    const mine = ++generation.current
    if (showSpinner) setLoading(true)
    try {
      const result = await fetcherRef.current()
      if (mine !== generation.current) return
      setData(result)
      setError(null)
    } catch (e) {
      if (mine !== generation.current) return
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      if (mine === generation.current) setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void run(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  // Background refresh: no spinner, so a realtime-triggered refetch doesn't
  // flash the whole page.
  const refetch = React.useCallback(() => run(false), [run])

  return { data, loading, error, refetch, setData }
}
